import { EventReward } from "../data/events";
import { OfferItem } from "../data/offers";
import { PASS_XP_SOURCES, PassTierReward, PassXpSource, SeasonTheme, SeasonsData } from "../data/seasons";
import { SECONDS_PER_DAY, utcDay } from "./orders";

/**
 * The season pass, "War Campaign" (MP-22): pure rules over seasons.json. Seasons are `daysPerSeason` UTC days counted
 * from a fixed epoch, so every device agrees with no server; a season's XP fills 30 tiers; the free track pays every
 * 3rd tier, the premium track (bought per season) every tier. XP is granted once per key (a battle's run key, an
 * order's uid, an event node, a Daily Road day). When the clock reaches a new season, `rollover` pays nothing itself:
 * it returns the ended season's earned, unclaimed rewards (marked claimed) for the caller to pay, and keeps a summary
 * the hub shows once. A clock set backwards never returns to an earlier season (the state's season is a floor).
 */

export type PassLevel = "free" | "premium" | "plus";
export type PassTrack = "free" | "premium";

/** A reward of one track at one tier. */
export interface TrackReward extends PassTierReward { readonly track: PassTrack }

/** The last rollover, until the player has seen it. */
export interface RolloverSummary {
  /** The season that ended (0-based). */
  readonly season: number;
  readonly tier: number;
  readonly pass: PassLevel;
  /** What was paid automatically, as (track, tier) of that season's tracks. */
  readonly rewards: readonly { readonly track: PassTrack; readonly tier: number }[];
}

export interface SeasonJson {
  v: number;
  /** The season this state is for (0-based); −1 before the first. */
  season: number;
  xp: number;
  pass: PassLevel;
  /** The store transaction that bought this season's pass ("" for none). */
  passTxn: string;
  free: number[];
  premium: number[];
  /** XP keys credited this season, per source (newest kept). */
  keys: Partial<Record<PassXpSource, string[]>>;
  /** Store transactions granted (newest kept): a re-delivery grants nothing. */
  txns: string[];
  /** Passes bought while one was already owned, applied at the next season's start. */
  banked: { level: Exclude<PassLevel, "free">; txn: string }[];
  summary: RolloverSummary | null;
}

export const SEASON_VERSION = 1;
/** Keys kept per XP source: a key is reported twice at most, right after its event (a battle's end screen and its exit). */
const KEYS_KEPT = 64;
/** Transaction ids kept: one or two a season. */
const TXNS_KEPT = 32;
/** Banked passes kept (more than a couple would be a store fault). */
const BANKED_KEPT = 4;
const LEVELS: readonly PassLevel[] = ["free", "premium", "plus"];
const KEY = /^[A-Za-z0-9_.:-]{1,96}$/;

// ------------------------------------------------------------------ the calendar

/** The season `now` falls in (0 = Season 1); negative before the epoch. */
export function seasonAt(data: SeasonsData, now: number): number {
  return Math.floor((utcDay(now) - data.epochDay) / data.daysPerSeason);
}
/** Whether season `n` exists (the pass has data and the epoch has passed). */
export function seasonLive(data: SeasonsData, n: number): boolean { return n >= 0 && data.tiers > 0; }
/** Unix seconds season `n` starts and ends (exclusive). */
export function seasonStartsAt(data: SeasonsData, n: number): number { return (data.epochDay + n * data.daysPerSeason) * SECONDS_PER_DAY; }
export function seasonEndsAt(data: SeasonsData, n: number): number { return seasonStartsAt(data, n + 1); }
/** Season `n`'s name and cosmetics (the list repeats after its last entry). */
export function seasonTheme(data: SeasonsData, n: number): SeasonTheme {
  const list = data.seasons;
  return list.length === 0 ? { name: "War Campaign", skin: "", title: "", plusTitle: "" } : list[((n % list.length) + list.length) % list.length];
}
/** The pass opens (shows and earns) once its unlock level is cleared. */
export function passUnlocked(data: SeasonsData, highestCleared: number): boolean { return data.tiers > 0 && highestCleared >= data.unlockAfterLevel; }

// ------------------------------------------------------------------ tiers and XP

export function maxPassXp(data: SeasonsData): number { return data.tiers * data.xpPerTier; }
/** Tiers reached with `xp` (0..tiers). */
export function tierOf(data: SeasonsData, xp: number): number { return Math.min(data.tiers, Math.floor(Math.max(0, xp) / data.xpPerTier)); }
/** XP into the tier being filled and the tier's size (the bar); full once the last tier is reached. */
export function tierProgress(data: SeasonsData, xp: number): { readonly into: number; readonly need: number; readonly maxed: boolean } {
  const tier = tierOf(data, xp);
  if (tier >= data.tiers) return { into: data.xpPerTier, need: data.xpPerTier, maxed: true };
  return { into: Math.max(0, xp) - tier * data.xpPerTier, need: data.xpPerTier, maxed: false };
}

/** XP a battle earns: a win, or a loss that cleared at least `lossMinStages` stages; an abandoned run earns none. */
export function battleXp(data: SeasonsData, b: { readonly won: boolean; readonly lost: boolean; readonly stagesCleared: number }): number {
  if (b.won) return data.xp.win;
  return b.lost && b.stagesCleared >= data.lossMinStages ? data.xp.loss : 0;
}

/** Every reward of both tracks in tier order (free first at a shared tier). */
export function trackRewards(data: SeasonsData): TrackReward[] {
  const out: TrackReward[] = [];
  for (let t = 1; t <= data.tiers; t++) {
    const f = data.free.find((r) => r.tier === t);
    if (f) out.push({ ...f, track: "free" });
    const p = data.premiumTrack.find((r) => r.tier === t);
    if (p) out.push({ ...p, track: "premium" });
  }
  return out;
}

/**
 * A reward as offer items, for the honest value (meta/offerValue.ts): gems, supplies, a speed-up and a chest have a gem
 * price; Forge Essence, Fusion Sigils and the cosmetic have none, so they count nothing.
 */
export function rewardItems(r: EventReward): OfferItem[] {
  const items: OfferItem[] = [];
  if (r.gems > 0) items.push({ kind: "gems", amount: r.gems, count: 1 });
  if (!r.resources.isZero()) items.push({ kind: "supplies", chests: r.resources, count: 1 });
  if (r.speedUpMinutes > 0) items.push({ kind: "speedUp", minutes: r.speedUpMinutes, count: 1 });
  if (r.chest !== null) items.push({ kind: "chest", rarity: r.chest, count: 1 });
  return items;
}
/** What Premium (and Premium+, the same items) pays over a season, as offer items. */
export function premiumItems(data: SeasonsData): OfferItem[] { return data.premiumTrack.flatMap((t) => rewardItems(t.reward)); }

// ------------------------------------------------------------------ the state

export class SeasonPass {
  season = -1;
  xp = 0;
  pass: PassLevel = "free";
  passTxn = "";
  readonly free = new Set<number>();
  readonly premium = new Set<number>();
  private readonly keys = new Map<PassXpSource, string[]>();
  private readonly txns: string[] = [];
  readonly banked: { level: Exclude<PassLevel, "free">; txn: string }[] = [];
  summary: RolloverSummary | null = null;

  get tier(): number { return this.tier$; }
  private tier$ = 0;
  private retier(data: SeasonsData): void { this.tier$ = tierOf(data, this.xp); }

  /** The premium track pays (this season's pass is bought). */
  get premiumOwned(): boolean { return this.pass !== "free"; }
  hasTxn(id: string): boolean { return this.txns.includes(id); }

  /** Adds `amount` XP once per (source, key), up to the last tier. Returns the XP added. */
  addXp(data: SeasonsData, source: PassXpSource, key: string, amount: number): number {
    if (this.season < 0 || !(amount > 0) || !KEY.test(key)) return 0;
    const list = this.keys.get(source) ?? [];
    if (list.includes(key)) return 0;
    list.push(key);
    if (list.length > KEYS_KEPT) list.splice(0, list.length - KEYS_KEPT);
    this.keys.set(source, list);
    const before = this.xp;
    this.xp = Math.min(maxPassXp(data), this.xp + Math.floor(amount));
    this.retier(data);
    return this.xp - before;
  }

  /** Reached and unclaimed rewards, in tier order (premium ones only with the pass). */
  claimable(data: SeasonsData): TrackReward[] {
    return trackRewards(data).filter((r) => r.tier <= this.tier && !this.claimed(r.track, r.tier) && (r.track === "free" || this.premiumOwned));
  }
  claimed(track: PassTrack, tier: number): boolean { return (track === "free" ? this.free : this.premium).has(tier); }

  /** Claims one reached reward once; null when not reached, locked or already claimed. The caller pays it. */
  claim(data: SeasonsData, track: PassTrack, tier: number): TrackReward | null {
    const r = this.claimable(data).find((x) => x.track === track && x.tier === tier);
    if (!r) return null;
    (track === "free" ? this.free : this.premium).add(tier);
    return r;
  }

  /**
   * A verified pass purchase: applied to this season (Premium+ adds its tiers; Premium+ over Premium upgrades), or
   * banked for the next season when this one already has it. "duplicate" for a transaction already granted.
   */
  applyPurchase(data: SeasonsData, level: Exclude<PassLevel, "free">, txn: string): "applied" | "banked" | "duplicate" {
    if (this.hasTxn(txn)) return "duplicate";
    this.txns.push(txn);
    if (this.txns.length > TXNS_KEPT) this.txns.splice(0, this.txns.length - TXNS_KEPT);
    if (this.season < 0 || this.pass === "plus" || (this.pass === "premium" && level === "premium")) {
      this.banked.push({ level, txn });
      if (this.banked.length > BANKED_KEPT) this.banked.splice(0, this.banked.length - BANKED_KEPT);
      return "banked";
    }
    this.setPass(data, level, txn);
    return "applied";
  }

  private setPass(data: SeasonsData, level: Exclude<PassLevel, "free">, txn: string): void {
    this.pass = level;
    this.passTxn = txn;
    if (level === "plus") {
      this.xp = Math.min(maxPassXp(data), this.xp + data.premiumPlus.tierSkip * data.xpPerTier);
      this.retier(data);
    }
  }

  /**
   * A refunded pass: this season's pass goes back to free (rewards already claimed stay, as spent gems do) and a
   * banked one is dropped. Returns whether anything changed.
   */
  revoke(txn: string): boolean {
    const b = this.banked.findIndex((x) => x.txn === txn);
    if (b >= 0) { this.banked.splice(b, 1); return true; }
    if (txn === "" || this.passTxn !== txn) return false;
    this.pass = "free";
    this.passTxn = "";
    return true;
  }

  /**
   * Moves the state to season `now` falls in. From a live season to a later one: the ended season's earned, unclaimed
   * rewards are marked claimed and returned (the caller pays them), the summary is kept, and the next season starts at
   * zero with the first banked pass. Null when the season has not changed (or the clock went back).
   */
  rollover(data: SeasonsData, now: number): { readonly ended: number; readonly rewards: readonly TrackReward[] } | null {
    const n = seasonAt(data, now);
    if (!seasonLive(data, n) || n <= this.season) return null;
    const ended = this.season;
    const owed = ended >= 0 ? this.claimable(data) : [];
    for (const r of owed) (r.track === "free" ? this.free : this.premium).add(r.tier);
    if (ended >= 0) this.summary = { season: ended, tier: this.tier, pass: this.pass, rewards: owed.map((r) => ({ track: r.track, tier: r.tier })) };
    this.season = n;
    this.xp = 0;
    this.tier$ = 0;
    this.pass = "free";
    this.passTxn = "";
    this.free.clear();
    this.premium.clear();
    this.keys.clear();
    const next = this.banked.shift();
    if (next) this.setPass(data, next.level, next.txn);
    return ended >= 0 ? { ended, rewards: owed } : null;
  }

  toJson(): SeasonJson {
    const keys: Partial<Record<PassXpSource, string[]>> = {};
    for (const s of PASS_XP_SOURCES) { const l = this.keys.get(s); if (l && l.length > 0) keys[s] = [...l]; }
    return {
      v: SEASON_VERSION, season: this.season, xp: this.xp, pass: this.pass, passTxn: this.passTxn, free: [...this.free].sort((a, b) => a - b),
      premium: [...this.premium].sort((a, b) => a - b), keys, txns: [...this.txns], banked: this.banked.map((b) => ({ ...b })), summary: this.summary,
    };
  }

  /** Leniently, field by field: malformed entries are dropped; an unreadable section is a fresh pass. */
  static fromJson(data: SeasonsData, j: unknown): SeasonPass {
    const p = new SeasonPass();
    if (typeof j !== "object" || j === null || Array.isArray(j)) return p;
    const src = j as Partial<SeasonJson>;
    const int = (v: unknown, lo: number, hi: number): number | null => (typeof v === "number" && Number.isInteger(v) && v >= lo && v <= hi ? v : null);
    p.season = int(src.season, -1, Number.MAX_SAFE_INTEGER) ?? -1;
    p.xp = int(src.xp, 0, maxPassXp(data)) ?? 0;
    p.retier(data);
    if (LEVELS.includes(src.pass as PassLevel)) p.pass = src.pass as PassLevel;
    if (typeof src.passTxn === "string" && src.passTxn.length <= 128) p.passTxn = src.passTxn;
    const tiers = (v: unknown): number[] => (Array.isArray(v) ? v.filter((t): t is number => int(t, 1, data.tiers) !== null) : []);
    for (const t of tiers(src.free)) p.free.add(t);
    for (const t of tiers(src.premium)) p.premium.add(t);
    const strings = (v: unknown, keep: number): string[] => (Array.isArray(v) ? v.filter((x): x is string => typeof x === "string" && KEY.test(x)).slice(-keep) : []);
    if (typeof src.keys === "object" && src.keys !== null) for (const s of PASS_XP_SOURCES) { const l = strings((src.keys as Record<string, unknown>)[s], KEYS_KEPT); if (l.length > 0) p.keys.set(s, l); }
    p.txns.push(...(Array.isArray(src.txns) ? src.txns.filter((x): x is string => typeof x === "string" && x.length > 0 && x.length <= 128).slice(-TXNS_KEPT) : []));
    if (Array.isArray(src.banked)) {
      for (const b of src.banked.slice(-BANKED_KEPT)) if (b && (b.level === "premium" || b.level === "plus") && typeof b.txn === "string") p.banked.push({ level: b.level, txn: b.txn });
    }
    p.summary = parseSummary(data, src.summary);
    return p;
  }
}

function parseSummary(data: SeasonsData, v: unknown): RolloverSummary | null {
  if (typeof v !== "object" || v === null) return null;
  const s = v as Partial<RolloverSummary>;
  if (typeof s.season !== "number" || !Number.isInteger(s.season) || s.season < 0 || !LEVELS.includes(s.pass as PassLevel)) return null;
  const rewards = Array.isArray(s.rewards) ? s.rewards.filter((r) => r && (r.track === "free" || r.track === "premium") && Number.isInteger(r.tier) && r.tier >= 1 && r.tier <= data.tiers) : [];
  return { season: s.season, tier: Math.max(0, Math.min(data.tiers, Number(s.tier) || 0)), pass: s.pass as PassLevel, rewards: rewards.map((r) => ({ track: r.track, tier: r.tier })) };
}
